/**
 * The sections a report can be about (bata-platform#686, ADR 9063).
 *
 * *Where the person was* (the route) and *what the report is about* (the section) are kept apart:
 * a person on the allocation screen may be reporting that the Cockpit's headline is wrong. The
 * form pre-fills the section from the route and lets them change it.
 *
 * **The list is the navigation's list.** Chapter screens come from `chapterNavigation()` — the
 * rail's and the command palette's own source — and the shell's screens from `shellTargets()`,
 * which the palette reads too. A screen added to a manifest appears here with no edit.
 *
 * **A section is an id, never a label.** The platform stores it and the team filters on it long
 * after the wording has changed, so it is derived from the screen's path — `/inseason/allocation`
 * is `inseason.allocation` — and never from the translated name. The platform has no screen
 * registry and does not bind to this manifest (issue 686): it checks only the shape,
 * {@link SECTION_ID_PATTERN}, and `sections.test.ts` proves every id this file can produce
 * matches it and that no two screens share one.
 */

import { chapterNavigation, screensOf } from '../../features/registry';
import { cockpitTarget, shellTargets, type ShellTargetAccess } from '../chrome/shell-targets';

/** What the platform accepts as a section id (issue 686): lowercase, digits, `.` `_` `-`, at most 64. */
export const SECTION_ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;

/** "General / not sure" — the report is about no particular screen. */
export const GENERAL_SECTION = 'general';

/** "New area" — an enhancement asking for a screen that does not exist. Offered for enhancements only. */
export const NEW_AREA_SECTION = 'new-area';

/** The Cockpit's id: its path is `/`, which has no segment to derive one from. */
const COCKPIT_SECTION = 'cockpit';

export interface SectionTarget {
  /** The stable id sent to the platform. */
  readonly id: string;
  /** The path of the screen, for choosing the one the person is on. */
  readonly to: string;
  readonly labelKey: string;
  /** The screen this is a view of, when it is one. */
  readonly parentLabelKey?: string;
  /** The chapter (or «Your account») the screen is listed under. */
  readonly chapterLabelKey: string;
}

/** The id for a screen's path: `/inseason/allocation` is `inseason.allocation`, `/` is `cockpit`. */
export function sectionIdOf(path: string): string {
  // A `:param` segment is a value the person chose, not part of where the screen is.
  const segments = path.split('/').filter((segment) => segment !== '' && !segment.startsWith(':'));
  return segments.length === 0 ? COCKPIT_SECTION : segments.join('.').toLowerCase();
}

/**
 * Every screen a report can be about, in the order a person reads the rail: the Cockpit, the
 * chapters, then the shell's own screens. A screen not offered in this release is left out — a
 * report about it would be about nothing the person can open; the general and new-area options
 * cover the wish.
 */
export function feedbackSections(access: ShellTargetAccess): readonly SectionTarget[] {
  const cockpit = cockpitTarget();
  const chapters = chapterNavigation()
    .filter((chapter) => !chapter.disabled)
    .flatMap((chapter) => chapter.entries.flatMap((parent) => screensOf([parent])
      .filter((entry) => entry.disabled !== true)
      .map((entry): SectionTarget => ({
        id: sectionIdOf(entry.to),
        to: entry.to,
        labelKey: entry.labelKey,
        ...(entry === parent ? {} : { parentLabelKey: parent.labelKey }),
        chapterLabelKey: chapter.navLabelKey,
      }))));
  const shell = [cockpit, ...shellTargets(access)].map((target): SectionTarget => ({
    id: sectionIdOf(target.to), to: target.to, labelKey: target.labelKey, chapterLabelKey: target.chapterLabelKey,
  }));
  // The Cockpit leads, the shell's account screens close the list.
  const [home, ...account] = shell;
  const seen = new Set<string>();
  return [...(home === undefined ? [] : [home]), ...chapters, ...account].filter((section) => {
    if (seen.has(section.id)) return false;
    seen.add(section.id);
    return true;
  });
}

/**
 * The section for the screen the person is on: the one whose path is `path`, else the one whose
 * path is the longest prefix of it (a drill, `/inseason/allocation/STORE-1`, belongs to the screen
 * it drills from), else «General».
 */
export function sectionForPath(path: string, sections: readonly SectionTarget[]): string {
  const exact = sections.find((section) => section.to === path);
  if (exact !== undefined) return exact.id;
  let best: SectionTarget | undefined;
  for (const section of sections) {
    if (section.to === '/' || !path.startsWith(`${section.to}/`)) continue;
    if (best === undefined || section.to.length > best.to.length) best = section;
  }
  return best?.id ?? GENERAL_SECTION;
}
